import React, { useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import { message as antdMessage } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { auth } from "../../Config/firebase-config";
import {
  getCurrentSubscriptionStatus,
  hasPaddleConfiguration,
  refreshAfterSuccessfulSubscription,
  startPaddleSubscriptionCheckout,
} from "../../actions/Billing/Paddle";
import type { AppDispatch, RootState } from "../../Redux/store";
import subscriptionPlanIcon from "../../Assets/Images/icons/subscription_plan.png";
import fireIcon from "../../Assets/Images/icons/fire.png";
import subscriptionBg from "../../Assets/Images/icons/subscription_background.jpg";
import "../../Assets/Styles/components/premiumGateDialog.css";

interface PremiumGateDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
}

const PLAN_FEATURES = [
  "Unlimited Batch watermarking",
  "Get full access to both Photo and Video apps under one subscription",
  "Sync templates on multiple devices",
  "Remove eZy watermark app logo from videos",
  "Maintain Resolution",
  "Use all recent logos",
  "Use all templates",
  "Sync templates on cloud",
  "Insert more than 3 watermarks at a time",
  "Insert Date/Time in autograph",
  "Save Autograph",
  "No Ads",
  "No purchase for stickers",
];

const MONTHLY_PRICE = "1.99";
const YEARLY_PRICE = "19.99";
const YEARLY_SAVE = "~$4";

const PremiumGateDialog: React.FC<PremiumGateDialogProps> = ({
  open,
  onClose,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<"monthly" | "yearly">("monthly");
  const [checkoutLoader, setCheckoutLoader] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch<AppDispatch>();
  const userSliceState = useSelector((state: RootState) => state.userSlice);
  const isLoggedIn = Boolean(Object.keys(userSliceState.user_Auth ?? {}).length);
  const userEmail =
    typeof userSliceState.user_Auth?.email === "string"
      ? userSliceState.user_Auth.email
      : auth.currentUser?.email ?? undefined;

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleSubscribe = async () => {
    if (!hasPaddleConfiguration()) {
      onClose();
      history.push("/pricing");
      return;
    }

    if (!isLoggedIn) {
      onClose();
      history.push("/pricing");
      return;
    }

    const loadingKey = `paddle-checkout-pgd-${selectedPlan}`;
    setCheckoutLoader(true);

    try {
      const { isPremium } = await getCurrentSubscriptionStatus();
      if (isPremium) {
        antdMessage.info("Your premium subscription is already active.");
        onClose();
        return;
      }

      const checkoutEvent = await startPaddleSubscriptionCheckout({
        plan: selectedPlan,
        email: userEmail,
        userId: auth.currentUser?.uid,
      });

      if (checkoutEvent.name !== "checkout.completed") {
        return;
      }

      antdMessage.loading({
        key: loadingKey,
        content: "Confirming your subscription…",
        duration: 0,
      });

      const refreshedStatus = await refreshAfterSuccessfulSubscription(dispatch);

      if (refreshedStatus.isPremium) {
        antdMessage.success({
          key: loadingKey,
          content: "Premium subscription activated successfully! 🎉",
          duration: 4,
        });
        onClose();
        history.push("/home");
      } else {
        antdMessage.success({
          key: loadingKey,
          content:
            "Payment completed. Premium access will appear shortly.",
          duration: 6,
        });
        onClose();
      }
    } catch (error) {
      antdMessage.error(
        error instanceof Error ? error.message : "Unable to start checkout."
      );
    } finally {
      setCheckoutLoader(false);
    }
  };

  const handleOneTimePayment = () => {
    onClose();
    history.push("/pricing");
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="pgd-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Subscription Plan"
    >
      <div className="pgd-modal">
        {/* Header */}
        <div className="pgd-header">
          <div className="pgd-header-title">
            <img src={subscriptionPlanIcon} alt="subscription plan" className="pgd-crown" />
            <span className="pgd-title-text">Subscription Plan</span>
          </div>
          <button
            className="pgd-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div className="pgd-body">
          {/* Features list */}
          <ul className="pgd-features-list">
            {PLAN_FEATURES.map((feat, i) => (
              <li key={i} className="pgd-feature-item">
                <img src={fireIcon} alt="" className="pgd-flame-icon" aria-hidden="true" />
                <span className="pgd-feature-text">{feat}</span>
              </li>
            ))}
          </ul>

          {/* Plan selector */}
          <div className="pgd-plans">
            {/* Monthly plan */}
            <button
              id="pgd-plan-monthly"
              className={`pgd-plan-card ${selectedPlan === "monthly" ? "pgd-plan-selected" : ""}`}
              onClick={() => setSelectedPlan("monthly")}
              aria-pressed={selectedPlan === "monthly"}
            >
              <div className="pgd-plan-left">
                <span className="pgd-plan-name">MONTHLY</span>
                <span className="pgd-plan-billing">Pay monthly</span>
              </div>
              <div className="pgd-plan-right">
                <span className="pgd-plan-price">${MONTHLY_PRICE}</span>
                <span className="pgd-plan-period">/mo</span>
              </div>
            </button>

            {/* Yearly plan */}
            <button
              id="pgd-plan-yearly"
              className={`pgd-plan-card ${selectedPlan === "yearly" ? "pgd-plan-selected" : ""}`}
              onClick={() => setSelectedPlan("yearly")}
              aria-pressed={selectedPlan === "yearly"}
            >
              <div className="pgd-plan-left">
                <span className="pgd-plan-name">YEARLY</span>
                <span className="pgd-plan-billing">Pay Annually</span>
              </div>
              <div className="pgd-plan-right">
                <span className="pgd-save-badge">save {YEARLY_SAVE}</span>
                <span className="pgd-plan-price pgd-yearly-price">
                  ${YEARLY_PRICE}
                </span>
                <span className="pgd-plan-period">/yr</span>
              </div>
            </button>
          </div>

          {/* Subscribe button */}
          <button
            id="pgd-subscribe-btn"
            className={`pgd-subscribe-btn ${checkoutLoader ? "pgd-subscribe-loading" : ""}`}
            onClick={() => void handleSubscribe()}
            disabled={checkoutLoader}
          >
            {checkoutLoader ? (
              <span className="pgd-btn-spinner" />
            ) : (
              "Subscribe Now"
            )}
          </button>

          <p className="pgd-secure-text">Cancel anytime. Secure checkout</p>

          {/* One-time payment option */}
          {/* <div className="pgd-otp-box">
            <p className="pgd-otp-title">Don&apos;t Want Subscription Plan?</p>
            <p className="pgd-otp-subtitle">
              Choose{" "}
              <button
                className="pgd-otp-link"
                onClick={handleOneTimePayment}
              >
                One time payment
              </button>{" "}
              to unlock limited features
            </p>
          </div> */}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PremiumGateDialog;
